import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Map, { Source, Layer, Popup, NavigationControl, FullscreenControl, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Layers, MapPin, X, Building2, Wifi, BarChart3, AlertTriangle, RotateCcw, Camera, Flame, Eye, EyeOff, Phone, GripHorizontal, Info } from 'lucide-react';
import { ConnectivityProfileCompact } from '../components/ConnectivityProfile';
import { useLAScope } from '../contexts/LAScopeContext';

// ── P2: Draggable floating panel with eye-icon collapse ──
function DraggablePanel({ id, title, subtitle, defaultPos, isCollapsed, onToggleCollapse, onStartDrag, position, children, testId, headerAccent = 'rgba(255,255,255,0.1)', maxWidth = 260, isDragging = false }) {
  const pos = position || defaultPos || { x: 16, y: 16 };
  return (
    <div
      data-testid={testId}
      className="absolute bg-black/85 backdrop-blur-md border border-white/10 rounded-lg shadow-xl animate-fade-in"
      style={{ left: pos.x, top: pos.y, maxWidth, minWidth: 220, userSelect: 'none', zIndex: isDragging ? 30 : 10 }}
    >
      {/* Header — doubles as drag handle */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10 cursor-move rounded-t-lg"
        style={{ background: `linear-gradient(90deg, ${headerAccent}, transparent)` }}
        onPointerDown={(e) => onStartDrag(id, e)}
        data-testid={`${testId}-handle`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <GripHorizontal className="w-3.5 h-3.5 text-white/30 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-xs font-extrabold text-white truncate">{title}</div>
            {subtitle && <div className="font-body text-[9px] text-white/40 truncate">{subtitle}</div>}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleCollapse(id); }}
          className="text-white/40 hover:text-white shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors"
          data-testid={`${testId}-eye`}
          title={isCollapsed ? 'Show panel body' : 'Hide panel body'}
        >
          {isCollapsed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
      {!isCollapsed && <div className="p-3">{children}</div>}
    </div>
  );
}

// Fix 1: Switch to Mapbox dark-v11
const MAP_STYLE = 'mapbox://styles/mapbox/dark-v11';
const INITIAL_VIEW = { longitude: -1.28, latitude: 54.57, zoom: 11 };
const MAP_BG_COLOR = '#0B0F16';  // Per spec — loading background

const LAYER_OPTIONS = [
  { id: 'off', label: 'None (Off)', icon: X },
  { id: 'digital_adoption', label: 'Digital Adoption Rate', icon: BarChart3 },
  { id: 'broadband_quality', label: 'Broadband Quality', icon: Wifi },
  { id: 'imd_deprivation', label: 'IMD Deprivation', icon: AlertTriangle },
  { id: 'business_density', label: 'Business Density', icon: Building2 },
  { id: 'connectivity_risk', label: 'Connectivity Risk', icon: MapPin },
  { id: 'pstn_risk', label: 'PSTN Switch-Off Risk', icon: Phone },
];

// P0 Spec: Five-tier digital visibility colours
const CATEGORY_COLORS = {
  active_visible: '#1B5E20',        // Deep Green — Fully Visible (score 4-5)
  partially_visible: '#66BB6A',     // Light Green — Partially Visible (score 2-3)
  trading_invisible: '#C62828',     // Trading Red — score 0-1 operating but invisible
  shell_company: '#424242',         // Dark Grey — non-trading holding cos
  dissolved_winding_down: '#BDBDBD',// Ash — dissolving / dormant
};
const CATEGORY_STROKE = {
  active_visible: '#2E7D32',
  partially_visible: '#81C784',
  trading_invisible: '#E53935',
  shell_company: '#616161',
  dissolved_winding_down: '#E0E0E0',
};
const CATEGORY_LABELS = {
  active_visible: 'Fully Visible',
  partially_visible: 'Partially Visible',
  trading_invisible: 'Trading Invisible',
  shell_company: 'Shell / Non-Trading',
  dissolved_winding_down: 'Dissolved / Winding Down',
};

const DISTRICT_BORDER_COLORS = {
  TS15: '#4FC3F7', TS16: '#BA68C8', TS17: '#4DB6AC',
  TS18: '#FF8A65', TS19: '#EF5350', TS20: '#66BB6A',
  TS21: '#FFD54F', TS22: '#AB47BC', TS23: '#26C6DA',
};

export default function MapView() {
  const navigate = useNavigate();
  const { laScope } = useLAScope();
  const [mapToken, setMapToken] = useState('');
  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [activeLayer, setActiveLayer] = useState('off');  // P2 AC-8: default OFF
  const [districtGeoJSON, setDistrictGeoJSON] = useState(null);
  const [businessGeoJSON, setBusinessGeoJSON] = useState(null);
  const [postcodes, setPostcodes] = useState([]);
  const [districtCounts, setDistrictCounts] = useState([]);  // P1 brief-exact V/P/I counts
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showTierExplanation, setShowTierExplanation] = useState(false);
  const [showBusinessPins, setShowBusinessPins] = useState(true);
  const [pinFilter, setPinFilter] = useState('all');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showLSOA, setShowLSOA] = useState(false);
  const [showWards, setShowWards] = useState(false);  // P1 AC-4: default OFF
  const [showLABoundaries, setShowLABoundaries] = useState(true);  // TVCA LA outlines on by default
  const [lsoaGeoJSON, setLsoaGeoJSON] = useState(null);
  const [wardGeoJSON, setWardGeoJSON] = useState(null);
  const [laGeoJSON, setLaGeoJSON] = useState(null);
  const [hoveredLSOA, setHoveredLSOA] = useState(null);
  const [showGigabit, setShowGigabit] = useState(false);
  const [gigabitGeoJSON, setGigabitGeoJSON] = useState(null);
  const [exporting, setExporting] = useState(false);
  // P2: per-panel collapsed state (eye icon) and floating positions
  const [collapsedPanels, setCollapsedPanels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sdr_map_panel_collapsed') || '{}'); } catch (e) { console.warn('MapView: bad panel state in storage', e); return {}; }
  });
  const [panelPositions, setPanelPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sdr_map_panel_positions') || '{}'); } catch (e) { console.warn('MapView: bad panel positions in storage', e); return {}; }
  });
  const [draggingPanel, setDraggingPanel] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Panel defaults — non-overlapping spec-compliant layout. Controls on the LEFT,
  // Legend & Quick Actions on the RIGHT so the control pin-filter buttons are always clickable.
  const PANEL_DEFAULTS = useMemo(() => ({
    controls: { x: 16, y: 16 },
    legend:   { x: typeof window !== 'undefined' ? window.innerWidth - 260 : 1000, y: 16 },
    actions:  { x: typeof window !== 'undefined' ? window.innerWidth - 280 : 980, y: 260 },
  }), []);

  const togglePanel = (id) => setCollapsedPanels(prev => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem('sdr_map_panel_collapsed', JSON.stringify(next)); } catch (e) { console.warn('MapView: could not persist panel state', e); }
    return next;
  });

  const startDrag = (id, e) => {
    const panel = e.currentTarget.parentElement;
    const rect = panel.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDraggingPanel(id);
  };

  useEffect(() => {
    if (!draggingPanel) return;
    const handleMove = (e) => {
      const mapContainer = document.querySelector('[data-testid="map-view"]');
      if (!mapContainer) return;
      const bounds = mapContainer.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - bounds.left - dragOffsetRef.current.x, bounds.width - 100));
      const y = Math.max(0, Math.min(e.clientY - bounds.top - dragOffsetRef.current.y, bounds.height - 40));
      setPanelPositions(prev => ({ ...prev, [draggingPanel]: { x, y } }));
    };
    const handleUp = () => {
      setDraggingPanel(null);
      try { localStorage.setItem('sdr_map_panel_positions', JSON.stringify(panelPositions)); } catch (e) { console.warn('MapView: could not persist panel positions', e); }
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [draggingPanel, panelPositions]);

  const resetPanelPositions = () => {
    setPanelPositions({});
    setCollapsedPanels({});
    try {
      localStorage.removeItem('sdr_map_panel_positions');
      localStorage.removeItem('sdr_map_panel_collapsed');
    } catch (e) { console.warn('MapView: could not clear panel layout', e); }
  };
  const mapRef = useRef(null);

  useEffect(() => { api.get('/config').then(r => setMapToken(r.data.mapbox_token)).catch(console.error); }, []);

  // Reload choropleth data when layer changes. 'off' = no fetch, blank.
  useEffect(() => {
    if (activeLayer === 'off') { setDistrictGeoJSON(null); }
    else { api.get(`/map/geojson?layer=${activeLayer}`).then(r => setDistrictGeoJSON(r.data)).catch(console.error); }
    api.get('/postcodes').then(r => setPostcodes(r.data)).catch(console.error);
  }, [activeLayer]);

  // P1 brief-exact V/P/I counts per TS district (sum must equal total)
  useEffect(() => { api.get('/map/district-counts').then(r => setDistrictCounts(r.data)).catch(console.error); }, []);

  useEffect(() => {
    let params = '';
    if (pinFilter === 'invisible') params = '&category=trading_invisible';
    else if (pinFilter === 'active') params = '&category=active_visible';
    else if (pinFilter === 'partial') params = '&min_score=2&max_score=3';  // P0 spec — score-based partial
    else if (pinFilter === 'shell') params = '&category=shell_company';
    else if (pinFilter === 'dissolved') params = '&category=dissolved_winding_down';
    if (laScope && laScope !== 'tvca') params += `&la=${laScope}`;
    api.get(`/map/businesses-geojson?${params}`).then(r => setBusinessGeoJSON(r.data)).catch(console.error);
  }, [pinFilter, laScope]);

  useEffect(() => { if (showLSOA && !lsoaGeoJSON) api.get('/map/lsoa-geojson').then(r => setLsoaGeoJSON(r.data)).catch(console.error); }, [showLSOA, lsoaGeoJSON]);
  // Pre-load wards so toggle works instantly
  useEffect(() => { api.get('/map/ward-geojson').then(r => setWardGeoJSON(r.data)).catch(console.error); }, []);
  useEffect(() => { api.get('/map/la-boundaries').then(r => setLaGeoJSON(r.data)).catch(console.error); }, []);
  useEffect(() => { if (showGigabit && !gigabitGeoJSON) api.get('/map/gigabit-postcodes').then(r => setGigabitGeoJSON(r.data)).catch(console.error); }, [showGigabit, gigabitGeoJSON]);

  // Fix 1: Force true dark background per P0 spec + suppress labels on map load
  const onMapLoad = useCallback((e) => {
    const map = e.target;
    if (typeof window !== 'undefined') window._map = map;
    ['background'].forEach(id => { if (map.getLayer(id)) { try { map.setPaintProperty(id, 'background-color', MAP_BG_COLOR); } catch (ex) { console.warn('MapView: background paint failed', ex); } } });
    ['land', 'land-structure-polygon', 'landuse', 'landcover'].forEach(id => {
      if (map.getLayer(id)) { try { map.setPaintProperty(id, 'fill-color', MAP_BG_COLOR); map.setPaintProperty(id, 'fill-opacity', 1); } catch (ex) { console.warn('MapView: fill paint failed', ex); } }
    });
    ['road-label', 'road-street-low', 'poi-label', 'transit-label', 'waterway-label', 'natural-label', 'airport-label', 'settlement-subdivision-label'].forEach(id => {
      if (map.getLayer(id)) { try { map.setLayoutProperty(id, 'visibility', 'none'); } catch (ex) { console.warn('MapView: layer hide failed', ex); } }
    });
    // Fix 2D: CSS canvas filters
    const canvas = map.getCanvas();
    if (canvas) { canvas.style.filter = 'saturate(1.4) brightness(1.1)'; }
  }, []);

  const onDistrictClick = useCallback((e) => { if (e.features?.length) { const p = e.features[0].properties; const pc = postcodes.find(pp => pp.district === p.district); if (pc) setSelectedDistrict(pc); } }, [postcodes]);
  const onBusinessClick = useCallback((e) => {
    if (e.features?.length) {
      setSelectedBusiness({ ...e.features[0].properties, lng: e.lngLat.lng, lat: e.lngLat.lat });
      setShowTierExplanation(false);  // always collapse on new pin
    }
  }, []);
  const onClusterClick = useCallback((e) => { if (!e.features?.length || !mapRef.current) return; const f = e.features[0]; const src = mapRef.current.getSource('business-source'); if (src) src.getClusterExpansionZoom(f.properties.cluster_id, (err, z) => { if (!err) setViewState(v => ({ ...v, longitude: f.geometry.coordinates[0], latitude: f.geometry.coordinates[1], zoom: z })); }); }, []);

  const interactiveLayerIds = useMemo(() => {
    const ids = ['district-fill'];
    if (showBusinessPins && !showHeatmap) ids.push('business-points', 'cluster-circles');
    if (showLSOA) ids.push('lsoa-fill');
    if (showWards) ids.push('ward-fill');
    return ids;
  }, [showBusinessPins, showHeatmap, showLSOA, showWards]);

  // Fix 4: Choropleth fill uses dynamic metric_value colour ramp — CHANGES per active layer
  const choroplethColorExpr = useMemo(() => {
    if (activeLayer === 'digital_adoption') return ['interpolate',['linear'],['get','metric_value'],0,'#FF2244',10,'#FF8C00',25,'#FFD54F',40,'#66BB6A',60,'#00FF88'];
    if (activeLayer === 'broadband_quality') return ['interpolate',['linear'],['get','metric_value'],20,'#FF2244',40,'#FF8C00',60,'#FFD54F',80,'#66BB6A',100,'#00CCFF'];
    if (activeLayer === 'imd_deprivation') return ['interpolate',['linear'],['get','metric_value'],1,'#FF2244',3,'#FF8C00',5,'#FFD54F',7,'#66BB6A',10,'#00CCFF'];
    if (activeLayer === 'business_density') return ['interpolate',['linear'],['get','metric_value'],200,'#00CCFF',1000,'#66BB6A',2000,'#FFD54F',3000,'#FF8C00',4000,'#FF2244'];
    if (activeLayer === 'connectivity_risk') return ['match',['get','metric_value'],1,'#00FF88',2,'#FF8C00',3,'#FF2244','#FF8C00'];
    if (activeLayer === 'pstn_risk') return ['interpolate',['linear'],['get','metric_value'],0,'#00FF88',30,'#FFD54F',60,'#FF8C00',80,'#FF2244'];
    return '#FF8C00';
  }, [activeLayer]);

  const districtFillLayer = useMemo(() => ({
    id: 'district-fill', type: 'fill',
    paint: {
      'fill-color': choroplethColorExpr,
      'fill-opacity': ['case', ['==', ['get', 'district'], hoveredDistrict || ''], 0.7, 0.55],
    },
  }), [choroplethColorExpr, hoveredDistrict]);

  // Fix 4: Ward/district borders — vivid on dark, width 3
  const districtOutlineLayer = useMemo(() => ({
    id: 'district-outline', type: 'line',
    paint: {
      'line-color': ['match', ['get', 'district'], ...Object.entries(DISTRICT_BORDER_COLORS).flat(), '#FFFFFF'],
      'line-width': ['case', ['==', ['get', 'district'], hoveredDistrict || ''], 4, 3],
      'line-opacity': 1.0,
    },
  }), [hoveredDistrict]);

  // Fix 4: District labels white on dark
  const districtLabelLayer = {
    id: 'district-labels', type: 'symbol',
    layout: { 'text-field': ['concat', ['get', 'district'], '\n', ['to-string', ['get', 'total_businesses']], ' biz'], 'text-size': 13, 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'], 'text-anchor': 'center', 'text-allow-overlap': true },
    paint: { 'text-color': '#FFFFFF', 'text-halo-color': '#000000', 'text-halo-width': 2 },
  };

  // P0 Spec: Colour determined by digital tier — score-based match then category for invisibles.
  // Business pin rendering. Rule: score>=4 → Fully Visible (Deep Green); 2-3 → Partially (Light Green);
  // score<2 → split between Trading Invisible / Shell / Dissolved per business_category.
  const pinColorExpr = useMemo(() => ([
    'case',
    ['>=', ['to-number', ['get', 'score']], 4], CATEGORY_COLORS.active_visible,
    ['>=', ['to-number', ['get', 'score']], 2], CATEGORY_COLORS.partially_visible,
    ['==', ['get', 'category'], 'shell_company'], CATEGORY_COLORS.shell_company,
    ['==', ['get', 'category'], 'dissolved_winding_down'], CATEGORY_COLORS.dissolved_winding_down,
    CATEGORY_COLORS.trading_invisible,  // default for score 0-1
  ]), []);
  const pinStrokeExpr = useMemo(() => ([
    'case',
    ['>=', ['to-number', ['get', 'score']], 4], CATEGORY_STROKE.active_visible,
    ['>=', ['to-number', ['get', 'score']], 2], CATEGORY_STROKE.partially_visible,
    ['==', ['get', 'category'], 'shell_company'], CATEGORY_STROKE.shell_company,
    ['==', ['get', 'category'], 'dissolved_winding_down'], CATEGORY_STROKE.dissolved_winding_down,
    CATEGORY_STROKE.trading_invisible,
  ]), []);

  // Fix 2C: Glow layer — renders BELOW main pins
  const businessGlowLayer = {
    id: 'business-glow', type: 'circle',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 10, 14, 20, 17, 30],
      'circle-opacity': 0.18,
      'circle-blur': 1.0,
      'circle-color': pinColorExpr,
    },
  };

  // Fix 2B: Main business pin layer — spec five-tier colours
  const businessPointLayer = {
    id: 'business-points', type: 'circle',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 8, 17, 12],
      'circle-opacity': 1.0,
      'circle-color': pinColorExpr,
      'circle-stroke-width': 1.5,
      'circle-stroke-opacity': 0.9,
      'circle-stroke-color': pinStrokeExpr,
      'circle-blur': 0.15,
    },
  };

  // Fix 3: Cluster circles — vivid, sized by count
  const clusterLayer = {
    id: 'cluster-circles', type: 'circle',
    filter: ['has', 'point_count'],
    paint: {
      // Align cluster colours to spec five-tier green palette: small=light-green, large=deep-green
      'circle-color': ['step', ['get', 'point_count'], '#66BB6A', 10, '#4CAF50', 50, '#2E7D32', 100, '#1B5E20'],
      'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 30, 100, 38],
      'circle-opacity': 0.92,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-opacity': 0.5,
      'circle-blur': 0.1,
    },
  };
  const clusterCountLayer = {
    id: 'cluster-count', type: 'symbol',
    filter: ['has', 'point_count'],
    layout: { 'text-field': '{point_count_abbreviated}', 'text-size': 13, 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'] },
    paint: { 'text-color': '#FFFFFF', 'text-halo-color': '#000000', 'text-halo-width': 1.5 },
  };

  // Fix 5: Heatmap — boosted intensity for dark background
  const heatmapLayer = {
    id: 'business-heatmap', type: 'heatmap', maxzoom: 16,
    paint: {
      'heatmap-weight': ['interpolate', ['linear'], ['get', 'score'], 0, 1, 2, 0.5, 5, 0.1],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 3.0],
      'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(0,0,0,0)', 0.15, 'rgba(255,34,68,0.4)', 0.35, 'rgba(255,140,0,0.55)',
        0.55, 'rgba(255,213,77,0.65)', 0.75, 'rgba(0,255,136,0.75)', 1, 'rgba(0,204,255,0.9)'],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 8, 20, 14, 35],
      'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0.85, 16, 0.65],
    },
  };

  const handleExportMap = useCallback(async () => {
    if (!mapRef.current) return;
    setExporting(true);
    try {
      const canvas = mapRef.current.getMap().getCanvas();
      const ec = document.createElement('canvas'); ec.width = canvas.width; ec.height = canvas.height;
      const ctx = ec.getContext('2d'); ctx.drawImage(canvas, 0, 0);
      ctx.fillStyle = 'rgba(0,0,0,0.85)'; ctx.fillRect(0, 0, ec.width, 56);
      ctx.fillStyle = '#00FF88'; ctx.font = 'bold 20px Syne, sans-serif';
      ctx.fillText('Adoptic · Tees Valley Digital Register — ' + LAYER_OPTIONS.find(l => l.id === activeLayer)?.label, 16, 36);
      ctx.font = '12px IBM Plex Mono, monospace'; ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillText('Exported: ' + new Date().toLocaleString() + ' | Infralytics | Nexintell Limited', 16, ec.height - 12);
      const link = document.createElement('a'); link.download = `stockton-map-${activeLayer}-${Date.now()}.png`; link.href = ec.toDataURL('image/png'); link.click();
    } catch (e) { console.error('Export failed:', e); }
    finally { setExporting(false); }
  }, [activeLayer]);

  const categoryLabel = (cat) => CATEGORY_LABELS[cat] || cat;

  // ── Unified pin-colour & label derivation — popup MUST match the pin ──
  // Rule: colour is driven by digital_score first, then falls back to category for score<2.
  // This returns the SAME cat-key the pin is coloured with so the tooltip is never contradictory.
  const displayTierFor = (biz) => {
    const score = Number(biz?.score ?? -1);
    if (score >= 4) return 'active_visible';
    if (score >= 2) return 'partially_visible';
    const raw = biz?.category;
    if (raw === 'shell_company') return 'shell_company';
    if (raw === 'dissolved_winding_down') return 'dissolved_winding_down';
    return 'trading_invisible';
  };

  // Plain-English rule explanation — shown in the "Why this tier?" popover.
  // Tells the user exactly why the business earned this tier based on the 5-signal footprint model.
  const tierExplanationFor = (biz) => {
    const tier = displayTierFor(biz);
    const score = Number(biz?.score ?? 0);
    const commonSignals = 'Score is assessed across five signals: website, Google Business profile, social media presence, business-directory listings, and customer reviews.';
    switch (tier) {
      case 'active_visible':
        return {
          title: `Why "Fully Visible"? (Score ${score}/5)`,
          body: `Strong digital presence — this business scored ${score} of 5 footprint signals. It has a healthy combination of website, Google Business listing, social media, directory presence, and/or customer reviews. Customers can find them easily. ${commonSignals}`,
        };
      case 'partially_visible':
        return {
          title: `Why "Partially Visible"? (Score ${score}/5)`,
          body: `Has some digital footprint but is missing 2-3 key signals (likely no website, or no Google Business profile, or no active social/reviews). A light-touch adoption intervention could move this business into "Fully Visible". ${commonSignals}`,
        };
      case 'shell_company':
        return {
          title: 'Why "Shell / Non-Trading"?',
          body: 'This is a Companies House entity that is not actively trading — typically a holding or dormant company. No intervention is required. These are excluded from most Economic Impact Estimator returns.',
        };
      case 'dissolved_winding_down':
        return {
          title: 'Why "Dissolved / Winding Down"?',
          body: 'This company has been dissolved or is in the process of winding down. It is not an intervention target. Record is retained for data continuity only.',
        };
      default:
        return {
          title: `Why "Trading Invisible"? (Score ${score}/5)`,
          body: `An active, trading business with no meaningful digital footprint — scored ${score} of 5. Customers searching online cannot find them. This is the highest-priority intervention category: a 2-hour digital starter session typically moves these businesses to "Partially Visible". ${commonSignals}`,
        };
    }
  };
  const resetView = () => setViewState(INITIAL_VIEW);

  if (!mapToken) return (
    <div className="flex items-center justify-center" data-testid="map-view" style={{ width: '100vw', height: 'calc(100vh - 64px)', background: MAP_BG_COLOR }}>
      <div className="p-8 text-center"><MapPin className="w-10 h-10 text-[#66BB6A]/40 mx-auto mb-3 animate-pulse" /><p className="font-body text-sm text-white/40">Initialising map…</p></div>
    </div>
  );

  return (
    <div className="relative" data-testid="map-view" style={{ width: '100vw', height: 'calc(100vh - 64px)', background: MAP_BG_COLOR }}>
      <style>{`.mapboxgl-canvas { filter: saturate(1.4) brightness(1.1); }`}</style>
      <Map
        ref={mapRef} {...viewState} onMove={e => setViewState(e.viewState)}
        mapboxAccessToken={mapToken} mapStyle={MAP_STYLE}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={interactiveLayerIds} preserveDrawingBuffer={true}
        onLoad={onMapLoad}
        onClick={(e) => {
          const bf = e.features?.find(f => f.layer?.id === 'business-points');
          const cf = e.features?.find(f => f.layer?.id === 'cluster-circles');
          const lf = e.features?.find(f => f.layer?.id === 'lsoa-fill');
          const wf = e.features?.find(f => f.layer?.id === 'ward-fill');
          const df = e.features?.find(f => f.layer?.id === 'district-fill');
          if (bf) onBusinessClick({ ...e, features: [bf] });
          else if (cf) onClusterClick({ ...e, features: [cf] });
          else if (lf) { const p = lf.properties; setSelectedDistrict({ district: p.lsoa_code, name: p.lsoa_name, total_businesses: p.business_count, digitally_active: p.digitally_active, digitally_invisible: p.digitally_invisible, avg_digital_score: p.avg_digital_score, imd_decile: p.imd_decile, avg_download_speed: p.avg_download_speed, _isLSOA: true }); }
          else if (wf) { const p = wf.properties; setSelectedDistrict({ district: p.ward_name, name: `Ward: ${p.ward_name}`, total_businesses: p.business_count, digitally_active: p.digitally_active, partially_visible: p.partially_visible, digitally_invisible: p.digitally_invisible, avg_digital_score: p.avg_digital_score, imd_decile: p.avg_imd_decile, avg_download_speed: p.avg_download_speed, _isWard: true }); }
          else if (df) onDistrictClick({ ...e, features: [df] });
        }}
        onMouseMove={(e) => {
          const df = e.features?.find(f => f.layer?.id === 'district-fill');
          setHoveredDistrict(df?.properties.district || null);
          setHoveredLSOA(e.features?.find(f => f.layer?.id === 'lsoa-fill')?.properties.lsoa_code || null);
        }}
        onMouseLeave={() => setHoveredDistrict(null)}
        cursor={hoveredDistrict ? 'pointer' : ''}
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />
        <ScaleControl position="bottom-right" />

        {/* LSOA — always mounted, visibility controlled */}
        {lsoaGeoJSON && (
          <Source id="lsoa-source" type="geojson" data={lsoaGeoJSON}>
            <Layer id="lsoa-fill" type="fill" layout={{ 'visibility': showLSOA ? 'visible' : 'none' }} paint={{
              'fill-color': ['interpolate', ['linear'], ['get', 'imd_decile'],
                1, '#FF2244', 2, '#FF6644', 3, '#FF8C00', 4, '#FFB833',
                5, '#FFD54F', 6, '#AADD44', 7, '#66BB6A', 8, '#26C6DA',
                9, '#42A5F5', 10, '#5C6BC0'],
              'fill-opacity': ['case', ['==', ['get', 'lsoa_code'], hoveredLSOA || ''], 0.7, 0.45],
            }} />
            <Layer id="lsoa-outline" type="line" layout={{ 'visibility': showLSOA ? 'visible' : 'none' }} paint={{
              'line-color': ['case', ['==', ['get', 'lsoa_code'], hoveredLSOA || ''], '#FFFFFF', 'rgba(255,255,255,0.3)'],
              'line-width': ['case', ['==', ['get', 'lsoa_code'], hoveredLSOA || ''], 2, 0.5],
            }} />
            <Layer id="lsoa-labels" type="symbol" minzoom={12} layout={{
              'visibility': showLSOA ? 'visible' : 'none',
              'text-field': ['concat', 'D', ['to-string', ['round', ['get', 'imd_decile']]]],
              'text-size': 10, 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
            }} paint={{ 'text-color': '#FFFFFF', 'text-halo-color': '#000000', 'text-halo-width': 1.5 }} />
          </Source>
        )}

        {/* Choropleth — uses dynamic colour ramp based on activeLayer; hidden when 'off' */}
        {activeLayer !== 'off' && districtGeoJSON && (
          <Source id="districts" type="geojson" data={districtGeoJSON} key={`district-${activeLayer}`}>
            <Layer {...districtFillLayer} />
            <Layer {...districtOutlineLayer} />
            <Layer {...districtLabelLayer} />
          </Source>
        )}

        {/* Ward boundaries — always mounted, visibility controlled by toggle */}
        {wardGeoJSON && (
          <Source id="ward-source" type="geojson" data={wardGeoJSON}>
            <Layer id="ward-fill" type="fill" layout={{ 'visibility': showWards ? 'visible' : 'none' }} paint={{ 'fill-color': 'transparent', 'fill-opacity': 0 }} />
            <Layer id="ward-outline" type="line" layout={{ 'visibility': showWards ? 'visible' : 'none' }} paint={{
              'line-color': '#FF8C00',
              'line-width': 3.0,
              'line-opacity': 1.0,
              'line-dasharray': [3, 1.5],
            }} />
            <Layer id="ward-labels" type="symbol" minzoom={11} layout={{
              'visibility': showWards ? 'visible' : 'none',
              'text-field': ['get', 'ward_name'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 11, 9, 14, 12],
              'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
              'text-anchor': 'center', 'text-max-width': 8,
            }} paint={{ 'text-color': '#FF8C00', 'text-halo-color': '#000000', 'text-halo-width': 1.5 }} />
          </Source>
        )}

        {/* TVCA Local Authority Boundaries — colored fill per LA + thick outline */}
        {laGeoJSON && (
          <Source id="la-source" type="geojson" data={laGeoJSON}>
            <Layer id="la-fill" type="fill" layout={{ 'visibility': showLABoundaries ? 'visible' : 'none' }} paint={{
              'fill-color': ['match', ['get', 'la_slug'],
                'stockton',      '#FE4A2A',
                'middlesbrough', '#F4B860',
                'redcar',        '#2D6E4E',
                'hartlepool',    '#1F5FA3',
                'darlington',    '#8B2A94',
                /* default */    '#5A5751',
              ],
              'fill-opacity': 0.08,
            }} />
            <Layer id="la-outline" type="line" layout={{ 'visibility': showLABoundaries ? 'visible' : 'none' }} paint={{
              'line-color': ['match', ['get', 'la_slug'],
                'stockton',      '#FE4A2A',
                'middlesbrough', '#C57F1F',
                'redcar',        '#2D6E4E',
                'hartlepool',    '#1F5FA3',
                'darlington',    '#8B2A94',
                /* default */    '#5A5751',
              ],
              'line-width': 2.5, 'line-opacity': 0.85,
            }} />
            <Layer id="la-labels" type="symbol" minzoom={8} maxzoom={13} layout={{
              'visibility': showLABoundaries ? 'visible' : 'none',
              'text-field': ['upcase', ['get', 'la_name']],
              'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 12, 16],
              'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
              'text-letter-spacing': 0.15,
            }} paint={{ 'text-color': '#FFFFFF', 'text-halo-color': '#000000', 'text-halo-width': 2 }} />
          </Source>
        )}

        {/* P1: Per-district Visible/Partial/Invisible count labels (brief AC-6). Appears with Wards toggle. */}
        {districtCounts.length > 0 && postcodes.length > 0 && (() => {
          const labelFC = {
            type: 'FeatureCollection',
            features: districtCounts.map(dc => {
              const pc = postcodes.find(p => p.district === dc.district);
              if (!pc?.lat || !pc?.lng) return null;
              const label = `${dc.district}\n✓ ${dc.visible}  ◐ ${dc.partial}  ✗ ${dc.invisible}\nΣ ${dc.total}`;
              return { type: 'Feature', properties: { label, district: dc.district }, geometry: { type: 'Point', coordinates: [pc.lng, pc.lat] } };
            }).filter(Boolean),
          };
          return (
            <Source id="district-counts-source" type="geojson" data={labelFC}>
              <Layer id="district-counts-bg" type="circle" layout={{ 'visibility': showWards ? 'visible' : 'none' }} paint={{
                'circle-radius': 42, 'circle-color': 'rgba(11,15,22,0.78)', 'circle-stroke-color': '#FF8C00', 'circle-stroke-width': 1.2, 'circle-blur': 0.15,
              }} />
              <Layer id="district-counts-label" type="symbol" layout={{
                'visibility': showWards ? 'visible' : 'none',
                'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                'text-anchor': 'center', 'text-justify': 'center', 'text-allow-overlap': true, 'text-ignore-placement': true,
              }} paint={{ 'text-color': '#FFFFFF', 'text-halo-color': '#000000', 'text-halo-width': 1.2 }} />
            </Source>
          );
        })()}

        {/* Business pins — glow + main */}
        {showBusinessPins && !showHeatmap && businessGeoJSON && (
          <Source id="business-source" type="geojson" data={businessGeoJSON} cluster={true} clusterMaxZoom={13} clusterRadius={40}>
            <Layer {...clusterLayer} />
            <Layer {...clusterCountLayer} />
            <Layer {...businessGlowLayer} />
            <Layer {...businessPointLayer} />
          </Source>
        )}

        {/* Heatmap — boosted */}
        {showHeatmap && businessGeoJSON && (
          <Source id="heatmap-source" type="geojson" data={businessGeoJSON}>
            <Layer {...heatmapLayer} />
          </Source>
        )}

        {/* Gigabit — always mounted, visibility controlled */}
        {gigabitGeoJSON && (
          <Source id="gigabit-source" type="geojson" data={gigabitGeoJSON}>
            <Layer id="gigabit-glow" type="circle" layout={{ 'visibility': showGigabit ? 'visible' : 'none' }} paint={{
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 6, 14, 10, 17, 16],
              'circle-color': ['interpolate', ['linear'], ['get', 'gigabit_pct'],
                0, '#FF2244', 30, '#FF8C00', 60, '#FFD54F', 80, '#00FF88', 100, '#00CCFF'],
              'circle-opacity': 0.3, 'circle-blur': 0.6,
            }} />
            <Layer id="gigabit-dots" type="circle" layout={{ 'visibility': showGigabit ? 'visible' : 'none' }} paint={{
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 14, 5, 17, 8],
              'circle-color': ['interpolate', ['linear'], ['get', 'gigabit_pct'],
                0, '#FF2244', 30, '#FF8C00', 60, '#FFD54F', 80, '#00FF88', 100, '#00CCFF'],
              'circle-opacity': 0.9, 'circle-stroke-width': 0.5, 'circle-stroke-color': 'rgba(255,255,255,0.4)',
            }} />
          </Source>
        )}

        {/* Business popup — tier derived from same score-based rule as pin colour */}
        {selectedBusiness && (() => {
          const tier = displayTierFor(selectedBusiness);
          const explanation = tierExplanationFor(selectedBusiness);
          return (
          <Popup longitude={selectedBusiness.lng} latitude={selectedBusiness.lat} onClose={() => { setSelectedBusiness(null); setShowTierExplanation(false); }} closeButton={true} closeOnClick={false} anchor="bottom" maxWidth="320px">
            <div className="p-2 font-body text-xs" data-testid="business-popup">
              <p className="font-display text-sm font-extrabold text-ink mb-1">{selectedBusiness.name}</p>
              <p className="text-ink/50 mb-1">{selectedBusiness.postcode} · {selectedBusiness.sector}</p>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider text-white" style={{ background: CATEGORY_COLORS[tier] || '#666' }} data-testid="business-popup-tier">
                  {categoryLabel(tier)}
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowTierExplanation(v => !v); }}
                    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/20 hover:bg-white/40 transition-colors ml-0.5"
                    title="Why this tier?"
                    data-testid="tier-explain-toggle"
                    aria-label="Why this tier?"
                  >
                    <Info className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
                  </button>
                </span>
                <span className="text-ink/40">Score: {selectedBusiness.score}/5</span>
              </div>
              {showTierExplanation && (
                <div
                  className="mb-2 p-2 rounded-[4px] bg-ink/5 border-l-2 animate-fade-in"
                  style={{ borderLeftColor: CATEGORY_COLORS[tier] || '#666' }}
                  data-testid="tier-explain-panel"
                >
                  <p className="font-display font-extrabold text-ink text-[10px] mb-1 uppercase tracking-wider">{explanation.title}</p>
                  <p className="font-body text-[10px] text-ink/70 leading-relaxed">{explanation.body}</p>
                </div>
              )}
              <div className="space-y-0.5 text-ink/50 text-[10px]">
                <p>{selectedBusiness.broadband} · {selectedBusiness.speed > 0 ? `${selectedBusiness.speed}Mbps` : 'N/A'}</p>
                <p>IMD: {selectedBusiness.imd}/10 · Risk: {selectedBusiness.risk || 'N/A'} · {selectedBusiness.ch_status}</p>
              </div>

              {/* Connectivity Profile — borough baseline + premise specifics */}
              <div className="mt-2 pt-2 border-t border-ink/10">
                <p className="font-body text-[8.5px] uppercase tracking-wider text-ink/40 mb-1.5">Connectivity at this premises</p>
                <ConnectivityProfileCompact
                  business={{
                    ofcom_gigabit_pct: selectedBusiness.ofcom_gigabit_pct,
                    ofcom_ufbb_pct: selectedBusiness.ofcom_ufbb_pct,
                    ofcom_below_uso_pct: selectedBusiness.ofcom_below_uso_pct,
                  }}
                />
                <p className="font-body text-[8px] text-ink/30 mt-1 italic">Source: Ofcom Connected Nations, July 2025</p>
              </div>
              <button onClick={() => navigate(`/business/${selectedBusiness.id}`)}
                className="mt-2 w-full text-center py-1.5 rounded bg-[#090909] text-white text-[10px] uppercase tracking-wider hover:bg-[#090909]/90 transition-all" data-testid="popup-view-profile">View Full Profile</button>
            </div>
          </Popup>
          );
        })()}
      </Map>

      {/* ── P2 Draggable Control Panels ── */}
      <DraggablePanel
        id="controls" testId="map-panel-controls" title="Intelligence Map" subtitle="Postcode-Level Digital Reality"
        defaultPos={PANEL_DEFAULTS.controls} position={panelPositions.controls} isDragging={draggingPanel === 'controls'}
        isCollapsed={collapsedPanels.controls} onToggleCollapse={togglePanel} onStartDrag={startDrag}
        headerAccent="rgba(102,187,106,0.12)" maxWidth={260}
      >
        <div className="flex items-center gap-2 mb-2">
          <Layers className="w-4 h-4 text-[#66BB6A]" strokeWidth={1.5} />
          <span className="font-body text-[10px] uppercase tracking-[0.12em] text-white/50 font-medium">Choropleth Layer</span>
        </div>
        <Select value={activeLayer} onValueChange={setActiveLayer}>
          <SelectTrigger data-testid="map-layer-select" className="font-body text-xs h-8 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LAYER_OPTIONS.map(l => <SelectItem key={l.id} value={l.id} className="font-body text-xs">{l.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="mt-4 pt-3 border-t border-white/10 space-y-2.5">
          {[
            { label: 'Business Pins', checked: showBusinessPins, set: v => { setShowBusinessPins(v); if(v) setShowHeatmap(false); }, id: 'toggle-business-pins', color: '#1B5E20' },
            { label: 'Heatmap', checked: showHeatmap, set: v => { setShowHeatmap(v); if(v) setShowBusinessPins(false); }, id: 'toggle-heatmap', color: '#FF8C00' },
            { label: 'IMD LSOAs', checked: showLSOA, set: setShowLSOA, id: 'toggle-lsoa', color: '#C62828' },
            { label: 'TVCA LA Boundaries', checked: showLABoundaries, set: setShowLABoundaries, id: 'toggle-la-boundaries', color: '#FE4A2A' },
            { label: 'Wards (V/P/I counts)', checked: showWards, set: setShowWards, id: 'toggle-wards', color: '#FF8C00' },
            { label: 'Gigabit', checked: showGigabit, set: setShowGigabit, id: 'toggle-gigabit', color: '#00CCFF' },
          ].map(t => (
            <div key={t.id} className="flex items-center justify-between">
              <span className="font-body text-[10px] uppercase tracking-[0.12em] font-medium" style={{ color: t.color }}>{t.label}</span>
              <Switch checked={t.checked} onCheckedChange={t.set} data-testid={t.id} />
            </div>
          ))}
        </div>

        {showBusinessPins && !showHeatmap && (
          <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
            <span className="font-body text-[9px] uppercase tracking-wider text-white/20">Filter pins</span>
            {[
              { id: 'all', label: 'All Businesses', color: '#FF8C00' },
              { id: 'invisible', label: 'Trading Invisible', color: CATEGORY_COLORS.trading_invisible },
              { id: 'partial', label: 'Partially Visible', color: CATEGORY_COLORS.partially_visible },
              { id: 'active', label: 'Fully Visible', color: CATEGORY_COLORS.active_visible },
              { id: 'shell', label: 'Shell / Non-Trading', color: CATEGORY_COLORS.shell_company },
              { id: 'dissolved', label: 'Dissolved', color: CATEGORY_COLORS.dissolved_winding_down },
            ].map(opt => (
              <button key={opt.id} data-testid={`pin-filter-${opt.id}`} onClick={() => setPinFilter(opt.id)}
                className={`w-full flex items-center gap-2 text-left px-2 py-1 rounded font-body text-[10px] transition-all ${pinFilter === opt.id ? 'bg-white/10 text-white font-medium' : 'text-white/40 hover:bg-white/5'}`}>
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: opt.color, boxShadow: pinFilter === opt.id ? `0 0 6px ${opt.color}` : 'none' }} />
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </DraggablePanel>

      <DraggablePanel
        id="legend" testId="map-panel-legend" title="Legend" subtitle="Colour reference"
        defaultPos={PANEL_DEFAULTS.legend} position={panelPositions.legend} isDragging={draggingPanel === 'legend'}
        isCollapsed={collapsedPanels.legend} onToggleCollapse={togglePanel} onStartDrag={startDrag}
        headerAccent="rgba(255,140,0,0.12)" maxWidth={240}
      >
        <div className="space-y-2" data-testid="map-legend">
          <div>
            <span className="font-body text-[9px] text-white/25 uppercase block mb-0.5">Business Digital Tier</span>
            {Object.entries(CATEGORY_COLORS).map(([cat, c]) => (
              <div key={cat} className="flex items-center gap-1.5 py-0.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: c, boxShadow: `0 0 4px ${c}` }} />
                <span className="font-body text-[9px] text-white/50">{categoryLabel(cat)}</span>
              </div>
            ))}
          </div>
          {activeLayer !== 'off' && (
            <div>
              <span className="font-body text-[9px] text-white/25 uppercase block mb-0.5">Choropleth Scale</span>
              <div className="flex items-center gap-1.5">
                <div className="w-20 h-2 rounded-sm" style={{ background: 'linear-gradient(to right, #C62828, #FF8C00, #FFD54F, #66BB6A, #00CCFF)' }} />
                <span className="font-body text-[8px] text-white/25">Low → High</span>
              </div>
            </div>
          )}
          {showWards && (
            <div className="pt-1 border-t border-white/10">
              <span className="font-body text-[9px] text-white/25 uppercase block mb-0.5">Ward Labels</span>
              <span className="font-body text-[9px] text-white/50 block">✓ Visible &nbsp; ◐ Partial &nbsp; ✗ Invisible</span>
              <span className="font-body text-[9px] text-white/40 block">Σ = total CH-registered</span>
            </div>
          )}
        </div>
      </DraggablePanel>

      <DraggablePanel
        id="actions" testId="map-panel-actions" title="Quick Actions" subtitle="View · Export · Reset"
        defaultPos={PANEL_DEFAULTS.actions} position={panelPositions.actions} isDragging={draggingPanel === 'actions'}
        isCollapsed={collapsedPanels.actions} onToggleCollapse={togglePanel} onStartDrag={startDrag}
        headerAccent="rgba(0,204,255,0.12)" maxWidth={260}
      >
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => setViewState({ longitude: -1.235, latitude: 54.57, zoom: 10 })} className="font-body text-[10px] rounded h-7 bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10" data-testid="fly-to-tees-valley"><MapPin className="w-3 h-3 mr-1" /> Tees Valley</Button>
          <Button variant="outline" size="sm" onClick={resetView} className="font-body text-[10px] rounded h-7 bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10" data-testid="reset-map-view"><RotateCcw className="w-3 h-3 mr-1" /> View</Button>
          <Button variant="outline" size="sm" onClick={handleExportMap} disabled={exporting} className="font-body text-[10px] rounded h-7 bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10" data-testid="export-map-btn"><Camera className="w-3 h-3 mr-1" /> PNG</Button>
          <Button variant="outline" size="sm" onClick={resetPanelPositions} className="font-body text-[10px] rounded h-7 bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10" data-testid="reset-panel-positions"><Layers className="w-3 h-3 mr-1" /> Reset Panels</Button>
        </div>
      </DraggablePanel>

      {/* ── Detail Panel ── */}
      {selectedDistrict && (
        <div className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[340px] z-10 bg-black/90 backdrop-blur-xl border border-white/10 rounded-lg p-4 animate-slide-in max-h-[70vh] overflow-y-auto" data-testid="district-detail-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-xl font-extrabold text-white">{selectedDistrict.district}</h3>
              <p className="font-body text-[11px] text-white/40">{selectedDistrict.name || selectedDistrict.ward_name || ''}</p>
            </div>
            <button onClick={() => setSelectedDistrict(null)} className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-white hover:bg-white/10 transition-all" data-testid="close-district-panel"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-1.5 rounded bg-[#00FF88]/10"><span className="font-display text-base font-extrabold text-[#00FF88]">{selectedDistrict.digitally_active || 0}</span><span className="font-body text-[9px] text-[#00FF88]/70 block">Active</span></div>
            <div className="text-center p-1.5 rounded bg-[#FF8C00]/10"><span className="font-display text-base font-extrabold text-[#FF8C00]">{selectedDistrict.partially_visible || 0}</span><span className="font-body text-[9px] text-[#FF8C00]/70 block">Partial</span></div>
            <div className="text-center p-1.5 rounded bg-[#FF2244]/10"><span className="font-display text-base font-extrabold text-[#FF2244]">{selectedDistrict.digitally_invisible || 0}</span><span className="font-body text-[9px] text-[#FF2244]/70 block">Invisible</span></div>
          </div>
          <dl className="space-y-1.5 font-body text-[11px]">
            <div className="flex justify-between"><dt className="text-white/40">Total</dt><dd className="text-white font-medium">{(selectedDistrict.total_businesses || 0).toLocaleString()}</dd></div>
            <div className="flex justify-between"><dt className="text-white/40">Adoption</dt><dd className="text-white font-medium">{selectedDistrict.adoption_rate || selectedDistrict.verified_adoption_rate || selectedDistrict.db_adoption_rate || 0}%</dd></div>
            <div className="flex justify-between"><dt className="text-white/40">Score</dt><dd className="text-white font-medium">{selectedDistrict.avg_digital_score || selectedDistrict.db_avg_score || 0}/5</dd></div>
            <div className="flex justify-between"><dt className="text-white/40">IMD</dt><dd className="text-white font-medium">{typeof selectedDistrict.imd_decile === 'number' ? Math.round(selectedDistrict.imd_decile) : selectedDistrict.imd_decile || '—'}/10</dd></div>
          </dl>
          <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
            <button onClick={() => navigate(`/search?district=${selectedDistrict.district}`)}
              className="flex-1 py-1.5 rounded bg-[#00FF88] text-black font-body text-[10px] uppercase tracking-wider font-bold hover:bg-[#00FF88]/90 transition-all text-center" data-testid="district-view-businesses">View Businesses</button>
            <button onClick={() => navigate(`/interventions?district=${selectedDistrict.district}`)}
              className="flex-1 py-1.5 rounded border border-white/20 text-white font-body text-[10px] uppercase tracking-wider hover:bg-white/5 transition-all text-center" data-testid="district-plan-intervention">Plan Intervention</button>
          </div>
        </div>
      )}

      {/* Hover tooltip */}
      {hoveredDistrict && !selectedDistrict && (
        <div className="absolute top-4 right-16 z-10 bg-black/80 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 pointer-events-none animate-fade-in" data-testid="district-hover-tooltip">
          <span className="font-display text-sm font-extrabold text-white">{hoveredDistrict}</span>
          <span className="font-body text-[10px] text-white/30 ml-2">{postcodes.find(p => p.district === hoveredDistrict)?.name || postcodes.find(p => p.district === hoveredDistrict)?.ward_name || ''}</span>
        </div>
      )}
    </div>
  );
}
